import { blockchainHandler } from '@/blockchain'
import { loadingController } from '@/components/global-loading/global-loading-controller'
import { snackController } from '@/components/snack-bar/snack-bar-controller'
import { SENCOND_PER_YEAR, Zero } from '@/constants'
import { FixedNumber } from '@ethersproject/bignumber'
import moment, { Duration } from 'moment'
import Web3 from 'web3'
import { bnHelper } from '../bignumber-helper'

export type StakingPoolType = 'SB' | 'STE' | 'USDT'

export class PoolInfo {
  poolToken!: string
  allocPoint!: FixedNumber
  lastRewardTime!: string
  accRewardPerShare!: string
  amount!: FixedNumber
  lockDuration!: Duration
}

const CHAIN_ID = process.env.VUE_APP_CHAIN_ID || ''
const web3 = blockchainHandler.getWeb3(CHAIN_ID)!

async function sendRequest(fx, from, gasLimit = '') {
  return await new Promise((resolve, reject) => {
    fx.send({ from, gasLimit })
      .on('receipt', result => resolve(result))
      .on('error', error => reject(error))
  })
}

export class StakingEventHandler {
  usdContract: any
  tokenContract: any
  routerContract: any
  stakeEventContract: any
  stakeEventAbi = require('./staking-event.abi.json')
  stakingPoolAddress = ''
  tokenAddress = ''
  rewardToken = ''
  tokenPerSecond = Zero
  totalAllocPoint = Zero
  tokenDecimals = 18
  poolInfo?: PoolInfo
  apr = Zero

  constructor(poolType: StakingPoolType) {
    if (poolType === 'SB') {
      this.tokenAddress = process.env.VUE_APP_SB_TOKEN_ADDRESS || ''
      this.tokenDecimals = 6
      this.stakingPoolAddress = process.env.VUE_APP_STAKING_EVENT_SB_POOL || ''
    } else if (poolType === 'STE') {
      this.tokenDecimals = 18
      this.tokenAddress = process.env.VUE_APP_STE_TOKEN_ADDRESS || ''
      this.stakingPoolAddress = process.env.VUE_APP_STAKING_EVENT_STE_POOL || ''
    } else if (poolType === 'USDT') {
      this.tokenDecimals = 18
      this.tokenAddress = process.env.VUE_APP_USD_ADDRESS || ''
      this.stakingPoolAddress = process.env.VUE_APP_STAKING_EVENT_USDT_POOL || ''
    }

    this.tokenContract = new web3.eth.Contract(require('../erc20.abi.json'), this.tokenAddress)
    this.stakeEventContract = new web3.eth.Contract(this.stakeEventAbi, this.stakingPoolAddress)
    this.routerContract = new web3.eth.Contract(require('../sb.router.abi.json'), process.env.VUE_APP_SB_ROUTER_ADDRESS)
  }

  async load() {
    try {
      loadingController.increaseRequest()
      const methods = this.stakeEventContract.methods
      const [
        rewardToken,
        tokenPerSecond,
        totalAllocPoint,
        tokenPoolInfo
      ] = await blockchainHandler.etherBatchRequest(web3, [
        methods.rewardToken(),
        methods.tokenPerSecond(),
        methods.totalAllocPoint(),
        methods.poolInfo(0)
      ])
      this.poolInfo = {
        poolToken: (tokenPoolInfo as any).poolToken,
        allocPoint: FixedNumber.from(`${(tokenPoolInfo as any).allocPoint}`),
        lastRewardTime: moment(`${(tokenPoolInfo as any).lastRewardTime}`, 'X').toISOString(),
        amount: FixedNumber.from(`${bnHelper.fromDecimals((tokenPoolInfo as any).amount, this.tokenDecimals)}`),
        lockDuration: moment.duration((tokenPoolInfo as any).lockDuration, 'second')
      } as PoolInfo
      this.rewardToken = rewardToken as string
      this.tokenPerSecond = FixedNumber.from(`${bnHelper.fromDecimals(`${tokenPerSecond}`, this.tokenDecimals)}`)
      this.totalAllocPoint = FixedNumber.from(totalAllocPoint)
    } catch (error) {
      snackController.commonError(error)
    } finally {
      loadingController.decreaseRequest()
    }
  }

  async getPoolUserInfo(poolId: number, walletAddress: string) {
    const methods = this.stakeEventContract.methods
    const [stakedAmount, pendingReward] = await blockchainHandler.etherBatchRequest(web3, [
      methods.getUserStakeBalance(poolId, walletAddress),
      methods.getRewardAmount(poolId, walletAddress)
    ])
    const {
      0: amount,
      1: rewardDebt,
      2: rewardAmount,
      3: withdrawingAmount,
      4: requestWithdrawTime
    } = await methods.userInfo(poolId, walletAddress).call()
    const res = {
      poolId: poolId,
      walletAddress: walletAddress,
      stakedAmount: FixedNumber.from(stakedAmount),
      pendingReward: pendingReward,
      amount,
      rewardDebt,
      rewardAmount,
      withdrawingAmount: bnHelper.fromDecimals(withdrawingAmount, this.tokenDecimals),
      requestWithdrawTime
    }

    return res
  }

  injectMetamask(web3: Web3) {
    if (web3) {
      this.stakeEventContract = new web3.eth.Contract(this.stakeEventAbi, this.stakingPoolAddress)
      this.tokenContract = new web3.eth.Contract(require('../erc20.abi.json'), this.tokenAddress)
      this.routerContract = new web3.eth.Contract(
        require('../sb.router.abi.json'),
        process.env.VUE_APP_SB_ROUTER_ADDRESS
      )
    }
  }

  async approved(account) {
    try {
      const allowance = await this.tokenContract.methods
        .allowance(account, process.env.VUE_APP_SB_ROUTER_ADDRESS)
        .call()
      return !!+web3.utils.fromWei(allowance)
    } catch (error) {
      console.error(error)
      return false
    }
  }

  async approve(account) {
    try {
      const f = this.tokenContract.methods.approve(
        process.env.VUE_APP_SB_ROUTER_ADDRESS,
        web3.utils.toWei(`${2 ** 64 - 1}`)
      )
      await sendRequest(f, account)
    } catch (error) {
      console.log('error', error)
      throw error
    }
  }

  async getTotalLockedAmount(poolId: number) {
    const { 4: amount } = await this.stakeEventContract.methods.poolInfo(poolId).call()
    return FixedNumber.from(`${bnHelper.fromDecimals(amount, this.tokenDecimals)}`)
  }

  async stake(poolId: number, amount: string, walletAddress: string) {
    const methods = this.stakeEventContract.methods
    const amountDecimals = bnHelper.toDecimalString(amount, this.tokenDecimals)
    const f = methods.stake(poolId, amountDecimals)
    await sendRequest(f, walletAddress)
  }

  async unStake(poolId: number, walletAddress: string) {
    const f = this.stakeEventContract.methods.unstake(poolId)
    await sendRequest(f, walletAddress)
  }

  async havest(poolId: number, walletAddress: string) {
    const methods = this.stakeEventContract.methods
    const f = methods.harvest(poolId)
    await sendRequest(f, walletAddress)
  }

  async withdraw(poolId: number, walletAddress: string) {
    const methods = this.stakeEventContract.methods
    const f = methods.withdraw(poolId)
    await sendRequest(f, walletAddress)
  }

  async getTokenPerSecond() {
    const methods = this.stakeEventContract.methods
    const [tokenPerSecond] = await blockchainHandler.etherBatchRequest(web3, [methods.tokenPerSecond()])
    this.tokenPerSecond = FixedNumber.from(`${bnHelper.fromDecimals(`${tokenPerSecond}`, this.tokenDecimals)}`)
  }
  userLength = 0
  async getUserLength(poolId) {
    const userLength = await this.stakeEventContract.methods.getPoolUserLength(poolId).call()
    this.userLength = userLength
    return userLength
  }
  async getPoolUserStakes(poolId) {
    const { 0: users, 1: stakeAmount } = await this.stakeEventContract.methods.getPoolUserStakes(poolId).call()

    const userStakes: any = []
    for (let index = 0; index < users.length; index++) {
      userStakes.push({
        address: users[index],
        amount: bnHelper.fromDecimals(stakeAmount[index], this.tokenDecimals)
      })
    }
    return userStakes
  }

  async setTokenPerSecond(tokenPerSecond, account) {
    const f = this.stakeEventContract.methods.setTokenPerSecond(
      bnHelper.toDecimalString(tokenPerSecond, this.tokenDecimals),
      true
    )
    await sendRequest(f, account)
  }

  async getTokenBalanceInContract() {
    const tokenBalance = await this.tokenContract.methods
      .balanceOf(this.stakingPoolAddress)
      .call()
      .then(x => bnHelper.fromDecimals(x, this.tokenDecimals))
    return tokenBalance
  }

  async removeErc20Amount(toAddress, amount, account) {
    const methods = this.stakeEventContract.methods
    const f = methods.removeErc20Amount(
      this.tokenAddress,
      toAddress,
      bnHelper.toDecimalString(amount, this.tokenDecimals)
    )
    await sendRequest(f, account)
  }

  async getStakeApy(tokenPerSecondInput: any = null) {
    const totalLockedAmount = await this.getTotalLockedAmount(0)

    let tokenPerSecond = this.tokenPerSecond
    if (tokenPerSecondInput) tokenPerSecond = tokenPerSecondInput

    const apr = totalLockedAmount.isZero()
      ? Zero
      : tokenPerSecond
          .mulUnsafe(SENCOND_PER_YEAR)
          .divUnsafe(this.totalAllocPoint)
          .mulUnsafe(this.poolInfo!.allocPoint)
          .divUnsafe(totalLockedAmount)
          .mulUnsafe(FixedNumber.from('100'))

    return apr
  }
}
